import { useState } from "react";
import type { InsightDocument } from "../../api/insights";
import { jurisdictionLabel, sectorLabel } from "../../lib/mandateLabels";
import { riskLevelColor } from "../../lib/riskVocabulary";
import { statusTag } from "./charts";
import { NAVY, shortDate } from "./primitives";

/**
 * Which instruments concern this organisation, and which one sets the regulatory deadline test.
 * Obligations and guidance in its jurisdictions and sectors come first; instruments addressed to
 * other sectors are listed (collapsed) so the reader can see they were considered.
 */
type Instrument = InsightDocument["mandates"]["instruments"][number];
const ORDER: Instrument["applicability"][] = ["obligation", "guidance", "reference", "other_sector"];
const GROUP: Record<Instrument["applicability"], string> = {
  obligation: "Addressed to you",
  guidance: "Guidance for your sector",
  reference: "International benchmarks",
  other_sector: "Addressed to other sectors in your jurisdictions",
};

function TagChip({ i }: { i: Instrument }) {
  const t = statusTag(i.applicability, i.binding);
  const strong = i.applicability === "obligation";
  return (
    <span className="inline-block text-[9.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm border whitespace-nowrap"
      style={{ background: strong ? NAVY : "#fff", color: strong ? "#fff" : NAVY, borderColor: i.applicability === "other_sector" ? "#cbd5e1" : NAVY }}>{t}</span>
  );
}

const KIND_LABEL: Record<string, string> = {
  plan: "plan", inventory: "inventory", begin: "begin migration", high_priority_complete: "high-priority migrated",
  all_complete: "all migrated", deprecate: "deprecated", disallow: "disallowed", procurement: "procurement",
};

function nextDated(i: Instrument) {
  const dated = i.milestones.filter((m) => m.date).sort((a, b) => a.date!.localeCompare(b.date!));
  return dated.find((m) => (m.days_remaining ?? 0) >= 0) ?? dated[dated.length - 1] ?? null;
}

export function OrganisationLine({ org }: { org: InsightDocument["mandates"]["organisation"] }) {
  const src = (s: string) => (s === "profile" ? "set in Settings" : s === "none" ? "not known" : "from the company record");
  return (
    <p className="text-[11px] text-ink-500" data-testid="org-context">
      Assessed for <strong className="text-ink-700">{org.jurisdictions.length > 0 ? org.jurisdictions.map(jurisdictionLabel).join(", ") : "no jurisdiction"}</strong> ({src(org.jurisdictions_source)})
      {" · "}<strong className="text-ink-700">{org.sectors.length > 0 ? org.sectors.map(sectorLabel).join(", ") : "no sector"}</strong> ({src(org.sectors_source)})
      {org.designations.length > 0 && <> · {org.designations.map(sectorLabel).join(", ")}</>}
      {" · "}deadline from {org.deadline_basis === "binding_only" ? "binding instruments only" : "binding instruments and guidance"}
    </p>
  );
}

export function MandatesPanel({ mandates, limit = 8 }: { mandates: InsightDocument["mandates"]; limit?: number }) {
  const [showOther, setShowOther] = useState(false);
  const d = mandates.deadline;
  const groups = ORDER.map((a) => ({ a, rows: mandates.instruments.filter((i) => i.applicability === a) })).filter((g) => g.rows.length > 0);
  const primary = groups.filter((g) => g.a !== "other_sector");
  const other = groups.find((g) => g.a === "other_sector");
  let budget = limit;
  return (
    <div className="space-y-3" data-testid="mandates-panel">
      <OrganisationLine org={mandates.organisation} />
      <div className="border-l-4 pl-3 py-1.5 text-xs" style={{ borderColor: d ? NAVY : riskLevelColor("High") }} data-testid="tested-deadline">
        {d ? (
          <>
            <strong className="text-ink-900">Deadline tested: {shortDate(d.date)}</strong>{" "}
            <span className="text-ink-700">{d.short_title ?? d.instrument}</span>
            <span className="text-ink-500"> · set by {d.set_by} · {jurisdictionLabel(d.jurisdiction)} · {statusTag(d.applicability, d.binding).toLowerCase()} · {Math.round(d.years * 10) / 10} yrs away</span>
            <div className="text-ink-700 mt-0.5" data-testid="deadline-basis"><strong>Track applied:</strong> {d.basis}</div>
          </>
        ) : (
          <span className="text-ink-700"><strong>No dated completion deadline applies.</strong> The instruments addressed to you set no date by which migration must be complete (any dates shown are for planning, inventory or procurement), so assets are tested against the planning year alone.</span>
        )}
      </div>
      {primary.length === 0 && <p className="text-xs text-ink-500">No catalogued instrument applies to this organisation's jurisdictions and sectors.</p>}
      {primary.map((g) => {
        const rows = g.rows.slice(0, Math.max(1, budget));
        budget -= rows.length;
        return (
          <div key={g.a}>
            <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">{GROUP[g.a]}</h4>
            <InstrumentTable rows={rows} />
            {g.rows.length > rows.length && <p className="text-[11px] text-ink-500 mt-1">+{g.rows.length - rows.length} more in the Risk Assessment Report — Detailed (Section 3)</p>}
          </div>
        );
      })}
      {other && (
        <div>
          <button type="button" className="text-[11px] text-accent hover:underline" onClick={() => setShowOther((v) => !v)} data-testid="toggle-other-sector">
            {showOther ? "Hide" : "Show"} {other.rows.length} instrument{other.rows.length === 1 ? "" : "s"} addressed to other sectors
          </button>
          {showOther && <div className="mt-1"><InstrumentTable rows={other.rows} /></div>}
        </div>
      )}
      <p className="text-[10.5px] text-ink-400">Catalogue verified {shortDate(mandates.catalogue_verified_on)}. Secondary-source rows should be checked against the primary text.</p>
    </div>
  );
}

function InstrumentTable({ rows }: { rows: Instrument[] }) {
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map((i) => {
          const tested = i.milestones.find((x) => x.sets_deadline) ?? null;
          // The tested milestone is the one shown when the instrument sets the deadline; otherwise the next dated one.
          const m = tested ?? nextDated(i);
          const sets = tested !== null;
          const miss = tested?.assets_projected_to_miss ?? null;
          return (
            <tr key={`${i.jurisdiction}-${i.instrument}-${i.sectors.join(",")}`} className="border-t border-ink-50 align-top">
              <td className="py-1.5 pr-2 w-[92px]"><TagChip i={i} /></td>
              <td className="py-1.5 pr-2">
                <a href={i.source_url} target="_blank" rel="noopener noreferrer" className="font-medium text-ink-900 hover:text-accent">{i.short_title ?? i.instrument}</a>
                <div className="text-[10.5px] text-ink-500 line-clamp-2" title={i.basis}>{jurisdictionLabel(i.jurisdiction)} · {i.track ?? i.applies_to}{i.verification === "secondary" ? " · secondary source" : ""}</div>
              </td>
              <td className="py-1.5 pl-2 text-right whitespace-nowrap">
                {m ? <span className={sets ? "font-semibold text-ink-900" : "text-ink-600"}>{shortDate(m.date!)}</span> : <span className="text-ink-400">No date</span>}
                {m?.kind && <div className="text-[10px] text-ink-500">{KIND_LABEL[m.kind] ?? m.kind.replace(/_/g, " ")}</div>}
                {sets && <div className="text-[10px]" style={{ color: miss && miss > 0 ? riskLevelColor("Critical") : NAVY }}>tested{miss !== null ? ` · ${miss} to miss` : ""}</div>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
